import React, {useEffect} from 'react'
import {
    questionnaireError, questionnaireLabel, questionnaireLabelDescription,
} from 'components/Embed/styles/questionnaire'

import ReactMarkdown from 'react-markdown'
import Select from 'components/Form/Select'
import {markdownProps} from 'components/Embed/helpers/markdown'

import {
    selectWrapper,
} from './styles'

import {styled} from 'stitches/config'
import {useTranslation} from 'react-i18next'


const StyledSelectWrapper = styled('div', selectWrapper)
const StyledLabel = styled('label', questionnaireLabel)
const StyledLabelDescription = styled('span', questionnaireLabelDescription)
const StyledError = styled('div', questionnaireError)

export interface QuestionnaireSelectProps {
    id: string
    name: string
    readableName?: string
    groupName?: string
    label?: string
    labelDescription?: string
    padding: string
    placeholder?: string
    required?: boolean
    value: any
    items: Array<{text: string, value: string}>
    forceValidation?: boolean
    invalid?: boolean
    invalidText?: string
    onChange: (params: {name: string, value: string, readableName?: string, groupName?: string, text: string}) => void
    setInvalidFields: (name: string, invalid: boolean) => void
}

export const QuestionnaireSelect = (props: QuestionnaireSelectProps) => {
    const {
        label, placeholder, items, id, value, onChange,
        setInvalidFields, labelDescription, invalid,
        invalidText, required, forceValidation, name,
        readableName, groupName, padding, ...other
    } = props

    const {t} = useTranslation()

    const [touched, setTouched] = React.useState(false)

    // eslint-disable-next-line no-extra-parens
    const isInvalid = (invalid && (touched || forceValidation || value)) || (!invalid && required && value === '' && (touched || forceValidation))

    useEffect(() => {
        if (forceValidation && required) {
            setInvalidFields(name, !!isInvalid)
        }
    }, [forceValidation, isInvalid, required])

    return (
        <StyledSelectWrapper>
            <StyledLabel htmlFor={id}>{label} <StyledLabelDescription>{labelDescription}</StyledLabelDescription></StyledLabel>
            <Select
                aria-describedby={isInvalid ? `${id}-error` : undefined}
                aria-invalid={isInvalid ? 'true' : 'false'}
                aria-required={required ? 'true' : 'false'}
                css={{
                    boxSizing: 'border-box',
                    width: '100%',
                }}
                cssSelect={{padding}}
                id={id}
                invalid={isInvalid}
                name={name}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    setTouched(true)
                    const selectedText = e.target.options[e.target.selectedIndex].text

                    onChange({
                        groupName,
                        name,
                        readableName,
                        text: selectedText || '',
                        value: e.target.value || '',
                    })
                }}
                value={value}
                {...other}
            >
                <option disabled value=''>
                    {placeholder || t('Select')}
                </option>
                {items.map((item) => (
                    <option key={item.value} value={item.value}>
                        {item.text}
                    </option>
                ))}
            </Select>
            {isInvalid ?
                <StyledError id={`${id}-error`} role='alert'>
                    <ReactMarkdown {...markdownProps}>
                        {invalidText || ''}
                    </ReactMarkdown>
                </StyledError>
                : null}
        </StyledSelectWrapper>
    )
}

QuestionnaireSelect.defaultProps = {
    forceValidation: false,
    groupName: '',
    invalid: false,
    invalidText: '',
    label: '',
    labelDescription: '',
    placeholder: '',
    readableName: '',
    required: false,
}
